import { Component } from '@theme/component';

/**
 * Announcement banner custom element that allows fading between content.
 * Based on the Slideshow component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} slideshowContainer
 * @property {HTMLElement[]} [slides]
 * @property {HTMLButtonElement} [previous]
 * @property {HTMLButtonElement} [next]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  #current = 0;

  /**
   * The interval ID for automatic playback.
   * @type {number|undefined}
   */
  #interval = undefined;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('mouseenter', this.suspend);
    this.addEventListener('mouseleave', this.resume);
    this.addEventListener('slide:hidden', this.#handleSlideHidden);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    // Blocks render `aria-hidden="false"` based on their index among ALL section blocks, but
    // a block can suppress or hide itself (an expired countdown), leaving the server-chosen
    // active slide gone or hidden with nothing visible. Re-point `current` at the first
    // non-hidden slide; the setter rewrites `aria-hidden` so exactly one slide shows.
    const slides = this.refs.slides ?? [];
    const activeIsHidden = slides[this.#current]?.hasAttribute('hidden');
    const nothingVisible = !slides.some(
      (slide) => !slide.hasAttribute('hidden') && slide.getAttribute('aria-hidden') === 'false'
    );
    if (activeIsHidden || nothingVisible) {
      const firstVisible = slides.findIndex((slide) => !slide.hasAttribute('hidden'));
      this.current = firstVisible === -1 ? this.#current : firstVisible;
    }

    this.#syncControls();

    this.play();
  }

  /**
   * Number of announcements currently visible (not hidden by a countdown expiry).
   * @returns {number}
   */
  get #visibleSlideCount() {
    const slides = this.refs.slides ?? [];
    return slides.filter((slide) => !slide.hasAttribute('hidden')).length;
  }

  /**
   * Hide the prev/next arrows (and stop autoplay) whenever there are fewer than two
   * visible announcements to move between; restore them otherwise.
   */
  #syncControls() {
    const multiple = this.#visibleSlideCount > 1;

    const arrows = this.querySelector('slideshow-arrows');
    if (arrows instanceof HTMLElement) arrows.hidden = !multiple;

    if (!multiple) this.pause();
  }

  next() {
    this.current += 1;
  }

  previous() {
    this.current -= 1;
  }

  /**
   * Starts automatic slide playback.
   * @param {number} [interval] - The time interval in seconds between slides.
   */
  play(interval = this.autoplayInterval) {
    if (!this.autoplay) return;

    this.paused = false;

    this.#interval = setInterval(() => {
      if (this.matches(':hover') || document.hidden) return;

      this.next();
    }, interval);
  }

  /**
   * Pauses automatic slide playback.
   */
  pause() {
    this.paused = true;
    this.suspend();
  }

  get paused() {
    return this.hasAttribute('paused');
  }

  set paused(paused) {
    this.toggleAttribute('paused', paused);
  }

  /**
   * Suspends automatic slide playback.
   */
  suspend() {
    clearInterval(this.#interval);
    this.#interval = undefined;
  }

  /**
   * Resumes automatic slide playback if autoplay is enabled.
   */
  resume() {
    if (!this.autoplay || this.paused) return;

    this.pause();
    this.play();
  }

  get autoplay() {
    return Boolean(this.autoplayInterval);
  }

  get autoplayInterval() {
    const interval = this.getAttribute('autoplay');
    const value = parseInt(`${interval}`, 10);

    if (Number.isNaN(value)) return undefined;

    return value * 1000;
  }

  get current() {
    return this.#current;
  }

  set current(current) {
    const slides = this.refs.slides ?? [];
    const count = slides.length;
    if (count === 0) {
      this.#current = current;
      return;
    }

    let relativeIndex = ((current % count) + count) % count;

    // Skip slides that have been hidden (e.g. a countdown slide whose timer hit zero).
    // Advance in the direction of travel until we land on a visible slide, giving up
    // after a full loop if every slide is hidden.
    const step = current < this.#current ? -1 : 1;
    for (let i = 0; i < count; i++) {
      if (!slides[relativeIndex]?.hasAttribute('hidden')) break;
      relativeIndex = ((relativeIndex + step) % count + count) % count;
    }

    this.#current = relativeIndex;

    slides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', `${index !== relativeIndex}`);
    });
  }

  /**
   * A slide hid itself (a countdown reached zero). If it was the one on screen, advance
   * to the next visible slide immediately instead of waiting for the next autoplay tick.
   */
  #handleSlideHidden = () => {
    const slides = this.refs.slides ?? [];
    if (slides[this.#current]?.hasAttribute('hidden')) {
      this.next();
    }

    // A slide just expired; if that leaves a single visible announcement, remove the arrows.
    this.#syncControls();
  };

  /**
   * Pause the slideshow when the page is hidden.
   */
  #handleVisibilityChange = () => (document.hidden ? this.pause() : this.resume());
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}
